// ---------------------------------------------------------------------------
// Audit Log - change details component
//
// Renders a per-field before/after diff for an audit log entry. When the
// `before_data` is empty (creation or export), the component falls back to
// rendering `after_data` as a flat key/value list.
// ---------------------------------------------------------------------------

interface ChangeDetailsProps {
  readonly before: Record<string, unknown> | null;
  readonly after: Record<string, unknown> | null;
}

export function ChangeDetails({ before, after }: ChangeDetailsProps) {
  if (!before && !after) {
    return (
      <p className="text-xs text-stone-400">Aucun detail de changement.</p>
    );
  }

  const allKeys = new Set([
    ...Object.keys(before ?? {}),
    ...Object.keys(after ?? {}),
  ]);

  const changedKeys = Array.from(allKeys).filter((key) => {
    const bVal = (before ?? {})[key];
    const aVal = (after ?? {})[key];
    return JSON.stringify(bVal) !== JSON.stringify(aVal);
  });

  if (changedKeys.length === 0 && after) {
    // Show all data if no before_data (creation or export)
    return (
      <div className="space-y-1.5">
        {Object.entries(after).map(([key, val]) => (
          <div key={key} className="text-xs">
            <span className="font-mono font-medium text-stone-600">{key}:</span>{" "}
            <span className="font-mono text-stone-800">
              {val != null ? String(val) : "null"}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (changedKeys.length === 0) {
    return (
      <p className="text-xs text-stone-400">Aucune difference detectee.</p>
    );
  }

  return (
    <div className="space-y-2">
      {changedKeys.map((key) => {
        const bVal = (before ?? {})[key];
        const aVal = (after ?? {})[key];
        return (
          <div key={key} className="text-xs">
            <span className="font-mono font-medium text-stone-600">{key}</span>
            <div className="ml-4 mt-0.5 flex flex-wrap items-center gap-1.5">
              {bVal !== undefined && (
                <span className="rounded bg-red-50 px-1.5 py-0.5 font-mono text-red-700 line-through">
                  {bVal != null ? String(bVal) : "null"}
                </span>
              )}
              {bVal !== undefined && <span className="text-stone-400">&rarr;</span>}
              <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-emerald-700">
                {aVal != null ? String(aVal) : "null"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
